import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  Clock, 
  CheckCircle, 
  FileText,
  PlayCircle,
  RefreshCw,
  Award,
  Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';
import usuarioProcesoService from '../../services/usuarioProcesoService';
import procesoService from '../../services/procesoService';
import perfilService from '../../services/perfilService';
import examenService from '../../services/examenService';
import perfilPruebaService from '../../services/perfilPruebaService';
import useAuthStore from '../../store/authStore';

export default function PostulanteDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [misAsignaciones, setMisAsignaciones] = useState([]);
  const [procesos, setProcesos] = useState([]);
  const [examenesDisponibles, setExamenesDisponibles] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar asignaciones del usuario actual desde el endpoint específico
      const misProcesosFiltrados = await usuarioProcesoService.getProcesosPorUsuario(user.id);
      setMisAsignaciones(misProcesosFiltrados);

      // Cargar procesos
      const allProcesos = await procesoService.getAllProcesos();
      setProcesos(allProcesos);

      // Cargar exámenes disponibles usando la tabla intermedia perfil-pruebas
      const perfilesResponse = await perfilService.getAllPerfiles({ page: 0, size: 1000 });
      const allPerfiles = perfilesResponse?.content || perfilesResponse || [];
      const allExamenes = await examenService.getAllExamenes();
      const asociacionesPruebas = await perfilPruebaService.getAll();
      
      // Obtener IDs de procesos asignados
      const procesosIds = misProcesosFiltrados.map(mp => mp.procesoId);
      
      // Filtrar perfiles que pertenecen a mis procesos
      const perfilesDeMisProcesos = allPerfiles.filter(
        p => procesosIds.includes(p.proceso?.id)
      );
      
      // Obtener IDs de perfiles
      const perfilesIds = perfilesDeMisProcesos.map(p => p.id);
      
      // Filtrar asociaciones que pertenecen a mis perfiles y tienen exámenes Raven
      const asociacionesRaven = asociacionesPruebas.filter(
        a => perfilesIds.includes(a.perfilId) && a.examenRavenId != null
      );
      
      // Obtener IDs de exámenes Raven asociados
      const examenesRavenIds = asociacionesRaven.map(a => a.examenRavenId);
      
      // Filtrar exámenes Raven que están en mis perfiles
      const examenesFiltrados = allExamenes.filter(
        e => examenesRavenIds.includes(e.id)
      );
      
      setExamenesDisponibles(examenesFiltrados);

    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader size="lg" />
      </div>
    );
  }

  const procesosActivos = misAsignaciones.filter(a => a.estado !== 'FINALIZADO').length;
  const procesosFinalizados = misAsignaciones.filter(a => a.estado === 'FINALIZADO').length;

  const stats = [
    {
      label: 'Procesos Asignados',
      value: misAsignaciones.length,
      icon: ClipboardList,
      color: 'bg-primary-500',
    },
    {
      label: 'En Curso',
      value: procesosActivos,
      icon: Clock,
      color: 'bg-yellow-500',
    },
    {
      label: 'Completados',
      value: procesosFinalizados,
      icon: CheckCircle,
      color: 'bg-green-500',
    },
    {
      label: 'Exámenes Disponibles',
      value: examenesDisponibles.length,
      icon: FileText,
      color: 'bg-blue-500',
    },
  ];

  const getEstadoBadge = (estado) => {
    const config = {
      'ASIGNADO': { variant: 'info', label: 'Asignado' },
      'EN_PROCESO': { variant: 'warning', label: 'En Proceso' },
      'FINALIZADO': { variant: 'success', label: 'Finalizado' },
    };
    const { variant = 'default', label = estado } = config[estado] || {};
    return <Badge variant={variant}>{label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-neutral-800">
              Mi Panel de Postulante
            </h1>
            <p className="text-neutral-600 mt-1">
              Bienvenido, {user.username}
            </p>
          </div>
          <Button onClick={() => loadData()} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-600">{stat.label}</p>
                  <p className="text-3xl font-bold text-neutral-800 mt-2">
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Mis Procesos Asignados */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-500" />
            Mis Procesos de Selección
          </h2>
          
          {misAsignaciones.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <ClipboardList className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
              <p className="text-lg font-medium">No tienes procesos asignados</p>
              <p className="text-sm mt-2">Espera a que un supervisor te asigne a un proceso de selección</p>
            </div>
          ) : (
            <div className="space-y-3">
              {misAsignaciones.map((asignacion) => {
                const proceso = procesos.find(p => p.id === asignacion.procesoId);
                
                return (
                  <Card key={asignacion.id} className="p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-neutral-800">
                            {proceso?.nombre || 'Proceso sin nombre'}
                          </h3>
                          {getEstadoBadge(asignacion.estado)}
                        </div>
                        <p className="text-sm text-neutral-600 mb-3">
                          {proceso?.descripcion || 'Sin descripción'}
                        </p>
                        {asignacion.observaciones && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-2">
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">Observaciones:</span> {asignacion.observaciones}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Exámenes Disponibles */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-secondary-500" />
            Exámenes Disponibles
          </h2>
          
          {examenesDisponibles.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <FileText className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
              <p className="text-lg font-medium">No hay exámenes disponibles</p>
              <p className="text-sm mt-2">Espera a que se te asignen exámenes para tu proceso de selección</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {examenesDisponibles.map((examen) => (
                <Card key={examen.id} className="p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-neutral-800 mb-1">
                        {examen.nombre}
                      </h3>
                      <p className="text-sm text-neutral-600 mb-3 line-clamp-2">
                        {examen.descripcion || 'Sin descripción'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-600 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Duración:
                      </span>
                      <span className="font-medium text-neutral-800">
                        {examen.tiempoMinutos} min
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-600">Ponderación:</span>
                      <span className="font-medium text-neutral-800">
                        {examen.porcentajePonderacion}%
                      </span>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full gap-2"
                    onClick={() => toast.info('Función de examen en desarrollo')}
                  >
                    <PlayCircle className="w-4 h-4" />
                    Iniciar Examen
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Información Importante */}
        <Card className="p-5 bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-200">
          <div className="flex items-start gap-3">
            <div className="bg-primary-100 p-2 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-800 mb-1">
                Instrucciones Importantes
              </h3>
              <ul className="text-sm text-neutral-700 space-y-1 list-disc list-inside">
                <li>Completa todos los exámenes asignados antes de la fecha límite</li>
                <li>Lee cuidadosamente las instrucciones antes de iniciar cada examen</li>
                <li>Una vez iniciado un examen, deberás completarlo en el tiempo establecido</li>
                <li>Asegúrate de tener una conexión estable a internet</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
